import { useIntl } from '@capsicum/common';
import { type BidAmountProps } from '@capsicum/features/fanshop/shared';
import { useCallback, useMemo, useState } from 'react';
import { type FieldError } from 'react-hook-form';

type UseBidAmountProps = Omit<BidAmountProps, 'chzCurrencyShortCode' | 'mainCurrencySymbol' | 'testID'>;

export const useBidAmount = ({
	chzRate,
	closeModal,
	country,
	initialBidAmount,
	mainCurrencyShortcode,
	minimumBid,
	onPress,
	shippingCost,
	userAmount
}: UseBidAmountProps) => {
	const { $t } = useIntl();
	const [amount, setAmount] = useState<number>(initialBidAmount || minimumBid);

	const validateAmount = useCallback(
		(bidAmount: number, shippingAmount: number, minBidAmount: number, walletAmount: number) => {
			const bidAndShipping = bidAmount + shippingAmount;

			if (bidAmount < minBidAmount) {
				return {
					message: texts.lessThanMinimumError(minBidAmount),
					type: 'required'
				} as FieldError;
			} else if (bidAndShipping > walletAmount) {
				return {
					message: texts.notEnoughChz,
					type: 'required'
				} as FieldError;
			}

			return undefined;
		},
		[amount, minimumBid, userAmount]
	);

	const texts = useMemo(
		() => ({
			buttonText: $t('rewards.auctions.bids.drawer.placeABid'),
			lessThanMinimumError: (minBidAmount: number) =>
				$t('rewards.auctions.bids.drawer.error.lowerThanMinimum', {
					minimumBid: `${minBidAmount} ${mainCurrencyShortcode}`
				}),
			notEnoughChz: $t('rewards.auctions.bids.drawer.error.notEnoughChz'),
			shippingSummary: $t('rewards.auctions.bids.drawer.shippingSummary', {
				country: country?.toUpperCase()
			}),
			title: $t('rewards.auctions.bids.drawer.title'),
			warning: $t('rewards.auctions.bids.drawer.warning'),
			warningEstimation: $t('rewards.auctions.bids.drawer.warningEstimation')
		}),
		[$t, mainCurrencyShortcode]
	);

	const getChzAmount = useCallback(() => {
		return (amount / chzRate).toFixed(2);
	}, [amount, chzRate]);

	const getChzShippingCost = useCallback(() => {
		return (shippingCost / chzRate).toFixed(2);
	}, [amount, chzRate]);

	const onButtonPress = useCallback(() => {
		onPress(amount);
		closeModal();
	}, [onPress, amount, closeModal]);

	return {
		amount,
		getChzAmount,
		getChzShippingCost,
		onButtonPress,
		setAmount,
		texts,
		validateAmount
	};
};
